import { useMemo } from 'react';
import { LoadingButton } from '@mui/lab';
import { Box, Button, ButtonBase, Card, Container, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { AnimatePresence, m } from 'framer-motion';
import DataGridCustom from 'src/components/data-grid-custom';
import HeaderBreadcrumbs from 'src/components/header-bread-crumbs/HeaderBreadcrumbs';
import Iconify from 'src/components/iconify';
import Page from 'src/components/page/page';
import { paths } from 'src/routes/paths';
import ThemesCategoriesDrawerFilters from './components/themes-categories-drawer-filters';
import { ThemesCategoriesTableToolbar } from './components/themes-categories-table-toolbar';
import DeletionImpactDialog from './components/category-delete-conflict-dialog';
import useThemesCategoriesList, { ThemesCategoriesTab } from './themes-categories-list.hook';

const tabContentMotion = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.18, ease: 'easeOut' },
};

type ThemesCategoriesTabsProps = {
  currentTab: ThemesCategoriesTab;
  tabs: Array<{ value: ThemesCategoriesTab; label: string; icon: string }>;
  onChangeTab: (tab: ThemesCategoriesTab) => void;
};

function ThemesCategoriesTabs({ currentTab, tabs, onChangeTab }: ThemesCategoriesTabsProps) {
  const activeTabIndex = tabs.findIndex((tab) => tab.value === currentTab);

  return (
    <Box
      role="tablist"
      aria-label="Abas de temas e categorias"
      sx={(theme) => ({
        position: 'relative',
        display: 'inline-grid',
        gridTemplateColumns: 'repeat(2, minmax(112px, 1fr))',
        width: { xs: '100%', sm: 'auto' },
        p: 0.35,
        mb: 2,
        overflow: 'hidden',
        borderRadius: 1,
        border: '1px solid',
        borderColor: alpha(theme.palette.grey[500], 0.18),
        backgroundColor: 'transparent',
      })}
    >
      <Box
        component={m.span}
        aria-hidden
        animate={{ x: `${Math.max(activeTabIndex, 0) * 100}%` }}
        transition={{ type: 'spring', stiffness: 480, damping: 36 }}
        sx={(theme) => ({
          position: 'absolute',
          zIndex: 0,
          top: 4,
          bottom: 4,
          left: 4,
          width: 'calc((100% - 8px) / 2)',
          overflow: 'hidden',
          borderRadius: 0.75,
          boxShadow: `0 8px 22px ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(
            100deg,
            ${alpha(theme.palette.primary.main, 0.15)} 0%,
            ${alpha(theme.palette.primary.main, 0.25)} 44%,
            ${alpha(theme.palette.primary.main, 0.15)} 100%
          )`,
          backgroundSize: '220% 100%',
          animation: 'themesCategoriesTabActiveColorFlow 4s ease-in-out infinite alternate',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            opacity: 0.8,
            transform: 'translateX(-115%)',
            background: `linear-gradient(
              105deg,
              transparent 0%,
              transparent 30%,
              ${alpha(theme.palette.common.white, 0.16)} 42%,
              ${alpha(theme.palette.common.white, 0.55)} 50%,
              ${alpha(theme.palette.common.white, 0.16)} 58%,
              transparent 70%,
              transparent 100%
            )`,
            animation: 'themesCategoriesTabReflection 2.8s ease-in-out infinite',
          },
          '@keyframes themesCategoriesTabActiveColorFlow': {
            from: { backgroundPosition: '0% 50%' },
            to: { backgroundPosition: '100% 50%' },
          },
          '@keyframes themesCategoriesTabReflection': {
            '0%': { transform: 'translateX(-115%)' },
            '45%, 100%': { transform: 'translateX(115%)' },
          },
        })}
      />

      {tabs.map((tab) => {
        const selected = currentTab === tab.value;

        return (
          <ButtonBase
            key={tab.value}
            role="tab"
            aria-selected={selected}
            onClick={() => onChangeTab(tab.value)}
            sx={(theme) => ({
              position: 'relative',
              zIndex: 1,
              minHeight: 38,
              minWidth: { xs: 0, sm: 128 },
              justifyContent: 'center',
              gap: 0.75,
              px: 1,
              borderRadius: 0.75,
              bgcolor: 'transparent',
              color: selected ? 'primary.main' : 'text.secondary',
              transition: theme.transitions.create(['color'], {
                duration: theme.transitions.duration.shorter,
              }),
              '&:hover': {
                color: selected ? 'primary.main' : 'text.primary',
                backgroundColor: 'transparent',
              },
            })}
          >
            <Box
              sx={(theme) => ({
                width: 22,
                height: 22,
                borderRadius: 0.65,
                display: 'grid',
                placeItems: 'center',
                color: selected ? 'primary.main' : 'text.secondary',
                backgroundColor: selected
                  ? alpha(theme.palette.primary.main, 0.08)
                  : alpha(theme.palette.grey[500], 0.06),
              })}
            >
              <Iconify icon={tab.icon} sx={{ width: 14, height: 14 }} />
            </Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: selected ? 800 : 600,
                color: selected ? 'primary.main' : 'text.primary',
                fontSize: 14,
                lineHeight: 1,
              }}
            >
              {tab.label}
            </Typography>
          </ButtonBase>
        );
      })}
    </Box>
  );
}

export default function ThemesCategoriesList() {
  const hook = useThemesCategoriesList();
  const isThemesTab = hook.currentTab === 'themes';
  const selectedIds = isThemesTab ? hook.selectedThemeIds : hook.selectedCategoryIds;
  const hasSelectedItems = selectedIds.length > 0;

  const handleCreate = () => {
    hook.navigate(isThemesTab ? paths.dashboard.themeCreate : paths.dashboard.categoryCreate);
  };

  const renderList = useMemo(
    () => (
      <DataGridCustom
        animatedEachRow
        customFilters={
          <ThemesCategoriesTableToolbar
            searchText={hook.searchText}
            selectedFiltersCount={hook.selectedFiltersCount}
            handleClickFilters={hook.handleFilterButtonClick}
            setSearchText={hook.handleSearchTextChange}
          />
        }
        excludeExportButton
        checkboxSelection
        columns={hook.columns}
        rows={hook.paginatedRows}
        totalRowsCount={hook.totalRowsCount}
        filteredRowsCount={hook.filteredRowsCount}
        initialVisibleColumns={{}}
        sortModel={hook.sortModel}
        paginationModel={hook.paginationModel}
        isLoading={hook.isTableLoading}
        onRowClick={hook.handleRowClick}
        rowSelectionModel={isThemesTab ? hook.selectedThemeIds : hook.selectedCategoryIds}
        onRowSelectionModelChange={
          isThemesTab ? hook.onThemeSelectionModelChange : hook.onCategorySelectionModelChange
        }
        onHeaderSelectionCheckboxChange={
          isThemesTab ? hook.onThemeHeaderSelectionChange : hook.onCategoryHeaderSelectionChange
        }
        setSortModel={hook.handleSortModelChange}
        setPaginationModel={hook.setPaginationModel}
        height={550}
      />
    ),
    [hook]
  );

  return (
    <Page title="Temas & Categorias">
      <Container maxWidth="xl">
        <HeaderBreadcrumbs
          heading="Temas & Categorias"
          links={[]}
          sx={{ mb: 3 }}
          action={
            <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}>
              {hasSelectedItems && (
                <LoadingButton
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={
                    <Iconify icon="mdi:trash-can-outline" sx={{ height: 16, width: 16 }} />
                  }
                  onClick={hook.onRequestBulkDelete}
                  loading={hook.isBulkDeleteLoading}
                  disabled={!hook.canDelete || hook.isBulkDeleteDisabled}
                  sx={{
                    bgcolor: 'common.white',
                    color: 'error.main',
                    borderColor: 'error.main',
                    '&:hover': {
                      bgcolor: 'common.white',
                      borderColor: 'error.dark',
                      color: 'error.dark',
                    },
                    '& .MuiButton-startIcon': {
                      marginRight: 0.5,
                    },
                  }}
                >
                  {isThemesTab ? 'Excluir Temas' : 'Excluir Categorias'}
                </LoadingButton>
              )}

              <Button
                size="small"
                variant="contained"
                startIcon={<Iconify icon="mdi:plus" sx={{ height: 16, width: 16 }} />}
                onClick={handleCreate}
                disabled={!hook.canCreate}
                sx={{
                  '& .MuiButton-startIcon': {
                    marginRight: 0.5,
                  },
                }}
              >
                {isThemesTab ? 'Cadastrar Tema' : 'Cadastrar Categoria'}
              </Button>
            </Stack>
          }
        />

        <ThemesCategoriesTabs
          currentTab={hook.currentTab}
          tabs={hook.TABS}
          onChangeTab={hook.handleChangeTab}
        />

        <AnimatePresence mode="wait" initial={false}>
          <Box key={hook.currentTab} component={m.div} {...tabContentMotion}>
            <Card sx={{ px: 2, pt: 1, pb: 0, boxShadow: 4 }}>
              <Box height="100%">{renderList}</Box>
            </Card>
          </Box>
        </AnimatePresence>
      </Container>
      <ThemesCategoriesDrawerFilters
        listLabel={isThemesTab ? 'Temas' : 'Categorias'}
        isOpen={hook.openFiltersDrawer}
        onClose={hook.onCloseFilters}
        onApplyFilters={hook.onApplyFilters}
        appliedFilters={hook.appliedFilters}
      />
      <DeletionImpactDialog
        open={Boolean(hook.deletionImpact)}
        entity={hook.deletionImpact?.entity || hook.currentTab}
        affectedBenefitsCount={hook.deletionImpact?.affectedBenefitsCount || 0}
        affectedEntityNames={hook.deletionImpact?.affectedEntityNames || []}
        benefits={hook.deletionImpact?.benefits || []}
        isDeleting={hook.isDeletingImpact}
        onClose={hook.onCloseDeletionImpact}
        onConfirm={hook.onConfirmDeletionImpact}
      />
    </Page>
  );
}
